import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { money } from '../../lib/format.js'
import { Badge, Field, Menu, MenuItem, SearchInput } from '../../components/ui.js'
import { toast } from '../../components/feedback.js'
import { BookIcon, DownloadIcon, SlidersIcon, XIcon } from '../../components/icons.js'
import { SEGMENT_LABEL, type Filters, type SegmentKey } from './types.js'

/**
 * The compact filter toolbar: search, status and segment inline; everything
 * else in a panel that only applies on "Apply filters", so a merchant can set
 * five fields without five reloads. Active filters show as removable chips.
 */

const NUMERIC_KEYS = ['valueMin', 'valueMax', 'withinHours', 'remindersMin', 'remindersMax'] as const
const FILTER_KEYS: Array<keyof Filters> = [
  'recovery', 'q', 'name', 'phone', 'email', 'product', 'valueMin', 'valueMax', 'from', 'to', 'withinHours', 'whatsapp',
  'emailStatus', 'remindersMin', 'remindersMax', 'payment', 'customerType', 'clicked', 'verified', 'segment', 'store',
]

export function filtersFromParams(params: URLSearchParams): Filters {
  const filters: Record<string, unknown> = { recovery: 'open' }
  for (const key of FILTER_KEYS) {
    const value = params.get(key)
    if (value === null || value === '') continue
    filters[key] = (NUMERIC_KEYS as readonly string[]).includes(key) ? Number(value) : value
  }
  return filters as unknown as Filters
}

export function filtersToParams(filters: Filters, into: URLSearchParams): void {
  for (const key of FILTER_KEYS) into.delete(key)
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '' || (key === 'recovery' && value === 'open')) continue
    into.set(key, String(value))
  }
}

/** For react-query keys and "did the filters change" checks. */
export function filtersKey(filters: Filters): string {
  return JSON.stringify(Object.entries(filters).filter(([, value]) => value !== undefined && value !== '').sort(([a], [b]) => a.localeCompare(b)))
}

const WITHIN: Array<{ hours: number; label: string }> = [
  { hours: 1, label: 'Last hour' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 168, label: 'Last 7 days' },
  { hours: 720, label: 'Last 30 days' },
]

function chipLabel(key: keyof Filters, value: unknown): string {
  switch (key) {
    case 'recovery':
      return value === 'recovered' ? 'Recovered' : 'All statuses'
    case 'q':
      return `“${String(value)}”`
    case 'valueMin':
      return `Cart ≥ ${money(Number(value))}`
    case 'valueMax':
      return `Cart ≤ ${money(Number(value))}`
    case 'withinHours':
      return WITHIN.find((entry) => entry.hours === value)?.label ?? `Last ${String(value)} h`
    case 'from':
      return `From ${String(value)}`
    case 'to':
      return `Until ${String(value)}`
    case 'whatsapp':
      return value === 'eligible' ? 'WhatsApp eligible' : 'Not WhatsApp eligible'
    case 'emailStatus':
      return { has_email: 'Has email', no_email: 'No email', eligible: 'Email eligible', emailed: 'Emailed', not_emailed: 'Not emailed' }[String(value)] ?? String(value)
    case 'remindersMin':
      return `≥ ${String(value)} reminders`
    case 'remindersMax':
      return `≤ ${String(value)} reminders`
    case 'payment':
      return { none: 'No payment attempt', pending: 'Payment pending', failed: 'Payment failed' }[String(value)] ?? String(value)
    case 'customerType':
      return value === 'returning' ? 'Returning customer' : 'New customer'
    case 'clicked':
      return value === 'yes' ? 'Clicked link' : 'Not clicked'
    case 'verified':
      return value === 'yes' ? 'Phone verified' : 'Phone unverified'
    case 'segment':
      return SEGMENT_LABEL[value as SegmentKey] ?? String(value)
    case 'store':
      return value === 'all' ? 'All stores' : 'Store filter'
    default:
      return `${key[0]!.toUpperCase()}${key.slice(1)}: ${String(value)}`
  }
}

export function activeChips(filters: Filters): Array<{ key: keyof Filters; label: string }> {
  return (Object.entries(filters) as Array<[keyof Filters, unknown]>)
    .filter(([key, value]) => value !== undefined && value !== '' && !(key === 'recovery' && value === 'open'))
    .map(([key, value]) => ({ key, label: chipLabel(key, value) }))
}

interface Preset {
  id: string
  name: string
  filters: Filters
}

export function FilterBar({
  filters,
  total,
  multiStore,
  canWrite,
  onChange,
  onExport,
  exporting,
}: {
  filters: Filters
  total: number | null
  multiStore: boolean
  canWrite: boolean
  onChange: (next: Filters) => void
  onExport: () => void
  exporting: boolean
}): ReactNode {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Filters>(filters)
  const [search, setSearch] = useState(filters.q ?? '')
  const queryClient = useQueryClient()

  useEffect(() => setDraft(filters), [filters])
  useEffect(() => setSearch(filters.q ?? ''), [filters.q])

  // Search applies itself after a pause; the panel waits for "Apply".
  useEffect(() => {
    if ((filters.q ?? '') === search.trim()) return undefined
    const timer = window.setTimeout(() => onChange({ ...filters, q: search.trim() || undefined }), 350)
    return () => window.clearTimeout(timer)
  }, [search, filters, onChange])

  const presets = useQuery({ queryKey: ['abandoned-presets'], queryFn: () => api<{ presets: Preset[] }>('/v1/abandoned/presets') })

  const savePreset = useMutation({
    mutationFn: (name: string) => api<Preset>('/v1/abandoned/presets', { method: 'POST', body: { name, filters } }),
    onSuccess: (preset) => {
      toast('ok', 'Filter saved', `“${preset.name}” is in your saved filters.`)
      void queryClient.invalidateQueries({ queryKey: ['abandoned-presets'] })
    },
    onError: (error) => toast('error', 'Could not save the filter', (error as Error).message),
  })

  const deletePreset = useMutation({
    mutationFn: (id: string) => api(`/v1/abandoned/presets/${id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['abandoned-presets'] }),
  })

  const set = <K extends keyof Filters>(key: K, value: Filters[K] | '' | undefined): void =>
    setDraft((current) => ({ ...current, [key]: value === '' ? undefined : value }))

  const num = (value: string): number | undefined => (value.trim() === '' || Number.isNaN(Number(value)) ? undefined : Number(value))

  const chips = activeChips(filters)
  const advancedCount = chips.filter((chip) => !['q', 'recovery', 'segment'].includes(chip.key)).length

  return (
    <>
      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, phone or email" />

        <select className="select" aria-label="Recovery status" value={filters.recovery} onChange={(event) => onChange({ ...filters, recovery: event.target.value as Filters['recovery'] })}>
          <option value="open">Still open</option>
          <option value="recovered">Recovered</option>
          <option value="all">All statuses</option>
        </select>

        <select className="select" aria-label="Segment" value={filters.segment ?? ''} onChange={(event) => onChange({ ...filters, segment: (event.target.value || undefined) as SegmentKey | undefined })}>
          <option value="">All segments</option>
          {Object.entries(SEGMENT_LABEL).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <button type="button" className={open ? 'btn btn--sm is-active' : 'btn btn--sm'} aria-expanded={open} aria-controls="abandoned-filter-panel" onClick={() => setOpen((value) => !value)}>
          <SlidersIcon size={15} />
          Filters
          {advancedCount > 0 && <Badge tone="brand">{advancedCount}</Badge>}
        </button>

        <span className="spacer" />

        <Menu label="Saved filters" align="right" bordered trigger={<span className="row" style={{ gap: 6 }}><BookIcon size={15} /> Saved</span>}>
          {(close) => (
            <>
              {(presets.data?.presets ?? []).length === 0 && <p className="menu__note">No saved filters yet.</p>}
              {(presets.data?.presets ?? []).map((preset) => (
                <div key={preset.id} className="preset-row">
                  <MenuItem
                    onClick={() => {
                      onChange(preset.filters)
                      close()
                    }}
                  >
                    {preset.name}
                  </MenuItem>
                  {canWrite && (
                    <button type="button" className="icon-btn" aria-label={`Delete saved filter ${preset.name}`} onClick={() => deletePreset.mutate(preset.id)}>
                      <XIcon size={14} />
                    </button>
                  )}
                </div>
              ))}
              {canWrite && (
                <MenuItem
                  onClick={() => {
                    close()
                    const name = window.prompt('Name this filter', chips.map((chip) => chip.label).slice(0, 3).join(', ') || 'My filter')
                    if (name && name.trim()) savePreset.mutate(name.trim().slice(0, 80))
                  }}
                >
                  Save current filters…
                </MenuItem>
              )}
            </>
          )}
        </Menu>

        {canWrite && (
          <button type="button" className="btn btn--sm" onClick={onExport} disabled={exporting || total === 0} title="Download the matching checkouts as CSV">
            <DownloadIcon size={15} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        )}
      </div>

      {open && (
        <div id="abandoned-filter-panel" className="filter-panel" role="region" aria-label="Advanced filters">
          <div className="filter-panel__grid">
            <Field label="Customer name">
              <input className="input" value={draft.name ?? ''} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label="Phone">
              <input className="input" inputMode="numeric" value={draft.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
            </Field>
            <Field label="Email">
              <input className="input" value={draft.email ?? ''} onChange={(e) => set('email', e.target.value)} />
            </Field>
            <Field label="Product">
              <input className="input" value={draft.product ?? ''} onChange={(e) => set('product', e.target.value)} />
            </Field>
            <Field label="Cart value from (₹)">
              <input className="input" inputMode="decimal" value={draft.valueMin ?? ''} onChange={(e) => set('valueMin', num(e.target.value))} />
            </Field>
            <Field label="Cart value to (₹)">
              <input className="input" inputMode="decimal" value={draft.valueMax ?? ''} onChange={(e) => set('valueMax', num(e.target.value))} />
            </Field>
            <Field label="Abandoned">
              <select className="select" value={draft.withinHours ?? ''} onChange={(e) => set('withinHours', num(e.target.value))}>
                <option value="">Any time</option>
                {WITHIN.map((entry) => (
                  <option key={entry.hours} value={entry.hours}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Abandoned between">
              <span className="row" style={{ gap: 6 }}>
                <input className="input" type="date" aria-label="From date" value={draft.from ?? ''} onChange={(e) => set('from', e.target.value)} />
                <input className="input" type="date" aria-label="To date" value={draft.to ?? ''} onChange={(e) => set('to', e.target.value)} />
              </span>
            </Field>
            <Field label="WhatsApp">
              <select className="select" value={draft.whatsapp ?? ''} onChange={(e) => set('whatsapp', e.target.value as Filters['whatsapp'])}>
                <option value="">Any</option>
                <option value="eligible">Eligible</option>
                <option value="ineligible">Not eligible</option>
              </select>
            </Field>
            <Field label="Email">
              <select className="select" value={draft.emailStatus ?? ''} onChange={(e) => set('emailStatus', e.target.value as Filters['emailStatus'])}>
                <option value="">Any</option>
                <option value="has_email">Has email</option>
                <option value="no_email">No email</option>
                <option value="eligible">Eligible for email</option>
                <option value="emailed">Already emailed</option>
                <option value="not_emailed">Not emailed yet</option>
              </select>
            </Field>
            <Field label="Reminders sent">
              <span className="row" style={{ gap: 6 }}>
                <input className="input" inputMode="numeric" aria-label="Minimum reminders" placeholder="min" value={draft.remindersMin ?? ''} onChange={(e) => set('remindersMin', num(e.target.value))} />
                <input className="input" inputMode="numeric" aria-label="Maximum reminders" placeholder="max" value={draft.remindersMax ?? ''} onChange={(e) => set('remindersMax', num(e.target.value))} />
              </span>
            </Field>
            <Field label="Payment">
              <select className="select" value={draft.payment ?? ''} onChange={(e) => set('payment', e.target.value as Filters['payment'])}>
                <option value="">Any</option>
                <option value="none">No payment attempt</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </Field>
            <Field label="Customer type">
              <select className="select" value={draft.customerType ?? ''} onChange={(e) => set('customerType', e.target.value as Filters['customerType'])}>
                <option value="">Any</option>
                <option value="new">New</option>
                <option value="returning">Returning</option>
              </select>
            </Field>
            <Field label="Link clicked">
              <select className="select" value={draft.clicked ?? ''} onChange={(e) => set('clicked', e.target.value as Filters['clicked'])}>
                <option value="">Any</option>
                <option value="yes">Clicked</option>
                <option value="no">Not clicked</option>
              </select>
            </Field>
            {multiStore && (
              <Field label="Store">
                <select className="select" value={draft.store ?? ''} onChange={(e) => set('store', e.target.value)}>
                  <option value="">This store</option>
                  <option value="all">All my stores</option>
                </select>
              </Field>
            )}
          </div>

          <div className="filter-panel__actions">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                onChange({ recovery: 'open' })
                setOpen(false)
              }}
            >
              Clear all
            </button>
            <button
              type="button"
              className="btn btn--sm btn--primary"
              onClick={() => {
                onChange({ ...draft, q: filters.q })
                setOpen(false)
              }}
            >
              Apply filters
            </button>
          </div>
        </div>
      )}

      {chips.length > 0 && (
        <div className="chips" aria-label="Active filters">
          {total !== null && (
            <span className="chips__count" aria-live="polite">
              {total.toLocaleString('en-IN')} match
            </span>
          )}
          {chips.map((chip) => (
            <button key={chip.key} type="button" className="chip" onClick={() => onChange({ ...filters, [chip.key]: chip.key === 'recovery' ? 'open' : undefined })} aria-label={`Remove filter ${chip.label}`}>
              {chip.label}
              <XIcon size={12} />
            </button>
          ))}
          <button type="button" className="chip chip--clear" onClick={() => onChange({ recovery: 'open' })}>
            Clear all
          </button>
        </div>
      )}
    </>
  )
}
